import { Link } from "react-router-dom";
import { formatUSD, resolveImage } from "@/lib/api";
import { Star, Plus, Heart, Clock } from "lucide-react";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function ProductCard({ product, compact = false }) {
  const today = WEEKDAYS[new Date().getDay()];
  const isToday = product.universe === "street_market" && product.category === today;
  const unavailable = product.available === false;

  return (
    <Link
      to={`/produit/${product.slug}`}
      className={`card-light group block h-full ${unavailable ? "opacity-70" : ""}`}
      data-testid={`product-card-${product.slug}`}
      onClick={(e) => { if (unavailable) { e.preventDefault(); } }}
    >
      <div className="relative aspect-square overflow-hidden bg-[var(--dd-bone)]">
        <img
          src={resolveImage(product.image)}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${unavailable ? "grayscale" : ""}`}
          loading="lazy"
        />
        {unavailable && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35" data-testid={`product-soldout-${product.slug}`}>
            <span className="bg-[var(--dd-coral)] text-white text-xs font-bold uppercase tracking-[0.22em] px-4 py-1.5 rounded-full shadow-lg">Sold out</span>
          </div>
        )}
        {product.old_price && (
          <div className="discount-badge">
            -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
          </div>
        )}
        <button onClick={(e) => { e.preventDefault(); }} className="absolute top-3 right-3 heart-btn">
          <Heart size={14} />
        </button>
        <div className="absolute top-3 right-12 bg-white/95 px-2 py-1 rounded-full text-[10px] font-bold text-[var(--dsf-text)] flex items-center gap-1">
          <Star size={9} fill="currentColor" className="text-[var(--dd-mustard)]" />
          {product.rating?.toFixed(1) || "4.8"}
        </div>
        {isToday && (
          <div
            className="absolute top-3 left-3 bg-[var(--dd-coral)] text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-md ring-2 ring-white animate-[pulse_2.5s_ease-in-out_infinite]"
            data-testid={`today-badge-${product.slug}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white" /> Today
          </div>
        )}
        {!isToday && product.badge && !product.old_price && (
          <div className="absolute top-3 left-3 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider">
            {product.badge}
          </div>
        )}
      </div>
      <div className={compact ? "p-3" : "p-4 md:p-5"}>
        <h3 className="font-semibold text-[var(--dd-green)] text-base md:text-lg leading-tight mb-1 group-hover:text-[var(--dd-mustard-dark)] transition-colors line-clamp-1">
          {product.name}
        </h3>
        <div className="text-xs text-[var(--dsf-text-muted)] mb-2">{product.category}</div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--dsf-text-muted)] mb-3">
          <Clock size={11} /> 18 min
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-[var(--dd-green)] text-lg md:text-xl">{formatUSD(product.price)}</span>
            {product.old_price && (
              <span className="text-xs text-[var(--dsf-text-muted)] line-through">{formatUSD(product.old_price)}</span>
            )}
          </div>
          <button
            className={`btn-add-round ${unavailable ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid={`pcard-add-${product.slug}`}
            disabled={unavailable}
            onClick={(e) => { if (unavailable) e.preventDefault(); }}
          >
            <Plus size={14} />
          </button>
        </div>
      </div>
    </Link>
  );
}
